import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["progressBar", "progressText", "resultsArea", "donutChart",
                     "colBand1", "colBand2", "colBand3", "colBand4", "colBand5",
                     "countBand1", "countBand2", "countBand3", "countBand4", "countBand5",
                     "goButton"]

  connect() {
    this.running = false
    this.compounds = JSON.parse(this.element.dataset.compounds || "[]")
  }

  go() {
    if (this.running) return
    this.running = true
    this.goButtonTarget.disabled = true
    this.goButtonTarget.textContent = "Running…"

    // Reset columns and counts
    for (let i = 1; i <= 5; i++) {
      this[`colBand${i}Target`].innerHTML = ""
      this[`countBand${i}Target`].textContent = "0"
    }

    // Show results area, reset progress bar, clear donut
    this.resultsAreaTarget.classList.remove("d-none")
    this.progressBarTarget.style.width = "0%"
    this.progressBarTarget.classList.add("progress-bar-striped", "progress-bar-animated")
    this.progressTextTarget.textContent = `0/${this.compounds.length}`
    this.donutChartTarget.innerHTML = ""
    this.donutChartTarget.style.opacity = 1

    // Shuffle compounds for variety
    const shuffled = [...this.compounds].sort(() => Math.random() - 0.5)
    const total = shuffled.length
    let completed = 0

    // Band counts for donut
    const bandCounts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }

    // Process each compound with staggered timing (~10s total)
    const baseInterval = 10000 / total

    shuffled.forEach((compound, index) => {
      const delay = baseInterval * (index + 1) + (Math.random() * baseInterval * 0.5)

      setTimeout(() => {
        completed++
        bandCounts[compound.band]++

        // Update progress bar
        const pct = Math.round((completed / total) * 100)
        this.progressBarTarget.style.width = `${pct}%`
        this.progressBarTarget.setAttribute("aria-valuenow", pct)
        this.progressTextTarget.textContent = `${completed}/${total}`

        // Update band count
        this[`countBand${compound.band}Target`].textContent = bandCounts[compound.band]

        // Add compound to appropriate column
        const col = this[`colBand${compound.band}Target`]
        const entry = document.createElement("div")
        entry.className = "cb-compound-entry"
        entry.innerHTML = this._entryHtml(compound)
        col.prepend(entry)

        // When all done, show donut chart
        if (completed === total) {
          this.progressBarTarget.classList.remove("progress-bar-animated")
          this.running = false
          this.goButtonTarget.disabled = false
          this.goButtonTarget.textContent = "Go (again)"
          setTimeout(() => this._showDonutChart(bandCounts, total), 500)
        }
      }, delay)
    })
  }

  _entryHtml(compound) {
    const name = this._escapeHtml(compound.name)
    const value = compound.value
    const unit = compound.unit_html || "μg/m<sup>3</sup>"
    const override = compound.override
      ? `<span class="badge bg-warning text-dark ms-1" style="font-size: 0.65rem;">Override</span>`
      : ""
    return `<span class="cb-compound-name">${name}</span>${override}<br>` +
           `<span class="cb-compound-value">${value} ${unit}</span>`
  }

  _showDonutChart(bandCounts, total) {
    const bands = [
      { band: 1, label: "1: Low",            color: "#28a745" },
      { band: 2, label: "2: Moderate",        color: "#3b82f6" },
      { band: 3, label: "3: High",            color: "#DAA520" },
      { band: 4, label: "4: Very High",       color: "#e67e22" },
      { band: 5, label: "5: Extremely High",  color: "#dc3545" },
    ]

    const size = 200
    const cx = size / 2
    const cy = size / 2
    const radius = 70
    const strokeWidth = 35
    const circumference = 2 * Math.PI * radius

    let arcs = ""
    let offset = 0
    let legendHtml = ""

    bands.forEach(b => {
      const count = bandCounts[b.band] || 0
      if (count === 0) {
        legendHtml += `<div class="cb-legend-item">
          <span class="cb-legend-swatch" style="background:${b.color};opacity:0.3;"></span>
          <span class="text-muted">${b.label}: 0 (0%)</span>
        </div>`
        return
      }
      const pct = count / total
      const dash = pct * circumference
      const gap = circumference - dash

      arcs += `<circle cx="${cx}" cy="${cy}" r="${radius}"
        fill="none" stroke="${b.color}" stroke-width="${strokeWidth}"
        stroke-dasharray="${dash} ${gap}"
        stroke-dashoffset="${-offset}"
        transform="rotate(-90 ${cx} ${cy})" />`
      offset += dash

      const pctLabel = Math.round(pct * 100)
      legendHtml += `<div class="cb-legend-item">
        <span class="cb-legend-swatch" style="background:${b.color};"></span>
        ${b.label}: ${count} (${pctLabel}%)
      </div>`
    })

    const svg = `<svg viewBox="0 0 ${size} ${size}" width="${size}" height="${size}">
      ${arcs}
      <text x="${cx}" y="${cy - 6}" text-anchor="middle" font-size="22" font-weight="bold" fill="#333">${total}</text>
      <text x="${cx}" y="${cy + 14}" text-anchor="middle" font-size="11" fill="#666">compounds</text>
    </svg>`

    this.donutChartTarget.innerHTML = `
      <div class="d-flex align-items-center justify-content-center gap-4 mt-3">
        <div>${svg}</div>
        <div class="cb-legend">${legendHtml}</div>
      </div>
    `
    this.donutChartTarget.style.opacity = 0
    requestAnimationFrame(() => {
      this.donutChartTarget.style.transition = "opacity 0.6s ease-in"
      this.donutChartTarget.style.opacity = 1
    })
  }

  _escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }
}
